import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { projectsApi } from "@/app/lib/api/projects";
import { Project } from "@/app/lib/supabase/types";
import { useAppStore } from "@/app/lib/stores/useAppStore";
import { createClient } from "@/app/utils/supabase/client";
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import { useAuth } from "@/app/lib/contexts/AuthContext";

const PROJECTS_STALE_TIME = 2 * 60 * 1000; // 2 minutes
const PROJECTS_GC_TIME = 48 * 60 * 60 * 1000; // 48 hours

const CLOSED_PROJECTS_STALE_TIME = 5 * 60 * 1000; // 5 minutes
const CLOSED_PROJECTS_GC_TIME = 4 * 60 * 60 * 1000; // 4 hours

const SINGLE_PROJECT_STALE_TIME = 2 * 60 * 1000; // 2 minutes
const SINGLE_PROJECT_GC_TIME = 1 * 60 * 60 * 1000; // 1 hour

export const projectQueryKeys = {
  all: ["projects"] as const,
  byCompany: (companyId: string) => [...projectQueryKeys.all, "company", companyId] as const,
  active: (companyId: string) => [...projectQueryKeys.byCompany(companyId), "active"] as const,
  closed: (companyId: string) => [...projectQueryKeys.byCompany(companyId), "closed"] as const,
  detail: (projectId: string) => [...projectQueryKeys.all, "detail", projectId] as const,
};

/**
 * Hook to fetch all projects (active and closed) for the current company
 */
export function useProjects() {
  const queryClient = useQueryClient();
  const { selectedCompanyId, selectedCompanyUser } = useAppStore();
  const { user } = useAuth();

  const query = useQuery<Project[]>({
    queryKey: projectQueryKeys.byCompany(selectedCompanyId || ""),
    queryFn: () => {
      if (!selectedCompanyId) throw new Error("No company selected");
      return projectsApi.getProjects(
        selectedCompanyId,
        user?.id,
        selectedCompanyUser?.role
      );
    },
    enabled: !!selectedCompanyId && !!user,
    staleTime: PROJECTS_STALE_TIME,
    gcTime: PROJECTS_GC_TIME,
  });

  // Set up real-time subscription
  useEffect(() => {
    if (!selectedCompanyId) return;

    const supabase = createClient();

    const channel = supabase
      .channel(`projects-${selectedCompanyId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "projects",
          filter: `company_id=eq.${selectedCompanyId}`,
        },
        (payload: RealtimePostgresChangesPayload<Project>) => {
          // Handle different events
          if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
            // Update the cache with the new/updated project
            queryClient.setQueryData<Project[]>(
              projectQueryKeys.byCompany(selectedCompanyId),
              (old) => {
                if (!old) return [payload.new as Project];

                const index = old.findIndex((p) => p.id === payload.new?.id);
                if (index > -1) {
                  // Update existing
                  const updated = [...old];
                  updated[index] = payload.new as Project;
                  return updated;
                } else {
                  // Add new
                  return [payload.new as Project, ...old];
                }
              }
            );

            // Also invalidate active/closed queries to refetch
            queryClient.invalidateQueries({
              queryKey: projectQueryKeys.active(selectedCompanyId)
            });
            queryClient.invalidateQueries({
              queryKey: projectQueryKeys.closed(selectedCompanyId)
            });
          } else if (payload.eventType === "DELETE") {
            // Remove from cache
            queryClient.setQueryData<Project[]>(
              projectQueryKeys.byCompany(selectedCompanyId),
              (old) => old?.filter((p) => p.id !== payload.old?.id) || []
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedCompanyId, queryClient]);

  return query;
}

/**
 * Hook to fetch only active projects for the current company
 */
export function useActiveProjects(options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();
  const { selectedCompanyId, selectedCompanyUser: selectedCompany } = useAppStore();
  const { user } = useAuth();

  const query = useQuery<Project[]>({
    queryKey: projectQueryKeys.active(selectedCompanyId || ""),
    queryFn: () => {
      if (!selectedCompanyId) throw new Error("No company selected");
      return projectsApi.getActiveProjects(
        selectedCompanyId,
        user?.id,
        selectedCompany?.role
      );
    },
    enabled: (!!selectedCompanyId && !!user) && (options?.enabled !== false),
    staleTime: PROJECTS_STALE_TIME,
    gcTime: PROJECTS_GC_TIME,
  });

  // Set up real-time subscription for active projects
  useEffect(() => {
    if (!selectedCompanyId || options?.enabled === false) return;

    const supabase = createClient();

    const channel = supabase
      .channel(`active-projects-${selectedCompanyId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "projects",
          filter: `company_id=eq.${selectedCompanyId}`,
        },
        (payload: RealtimePostgresChangesPayload<Project>) => {
          console.log("Active projects real-time update:", payload);

          if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
            const project = payload.new as Project;

            // Update cache if it's an active project
            if (!project.is_closed) {
              queryClient.setQueryData<Project[]>(
                projectQueryKeys.active(selectedCompanyId),
                (old) => {
                  if (!old) return [project];

                  const index = old.findIndex((p) => p.id === project.id);
                  if (index > -1) {
                    // Update existing
                    const updated = [...old];
                    updated[index] = project;
                    return updated;
                  } else {
                    // Add new
                    return [project, ...old];
                  }
                }
              );
            } else {
              // If project is now closed, remove it from active projects
              queryClient.setQueryData<Project[]>(
                projectQueryKeys.active(selectedCompanyId),
                (old) => old?.filter((p) => p.id !== project.id) || []
              );
            }

            // Also invalidate the closed projects query in case status changed
            queryClient.invalidateQueries({
              queryKey: projectQueryKeys.closed(selectedCompanyId)
            });
          } else if (payload.eventType === "DELETE") {
            // Remove from cache
            queryClient.setQueryData<Project[]>(
              projectQueryKeys.active(selectedCompanyId),
              (old) => old?.filter((p) => p.id !== payload.old?.id) || []
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedCompanyId, queryClient, options?.enabled]);

  return query;
}

/**
 * Hook to fetch only closed projects for the current company
 */
export function useClosedProjects(options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();
  const { selectedCompanyId, selectedCompanyUser: selectedCompany } = useAppStore();
  const { user } = useAuth();

  const query = useQuery<Project[]>({
    queryKey: projectQueryKeys.closed(selectedCompanyId || ""),
    queryFn: () => {
      if (!selectedCompanyId) throw new Error("No company selected");
      return projectsApi.getClosedProjects(
        selectedCompanyId,
        user?.id,
        selectedCompany?.role
      );
    },
    enabled: (!!selectedCompanyId && !!user) && (options?.enabled !== false),
    staleTime: CLOSED_PROJECTS_STALE_TIME,
    gcTime: CLOSED_PROJECTS_GC_TIME,
  });

  // Set up real-time subscription for closed projects
  useEffect(() => {
    if (!selectedCompanyId || options?.enabled === false) return;

    const supabase = createClient();

    const channel = supabase
      .channel(`closed-projects-${selectedCompanyId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "projects",
          filter: `company_id=eq.${selectedCompanyId}`,
        },
        (payload: RealtimePostgresChangesPayload<Project>) => {
          console.log("Closed projects real-time update:", payload);

          if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
            const project = payload.new as Project;

            // Update cache if it's a closed project
            if (project.is_closed === true) {
              queryClient.setQueryData<Project[]>(
                projectQueryKeys.closed(selectedCompanyId),
                (old) => {
                  if (!old) return [project];

                  const index = old.findIndex((p) => p.id === project.id);
                  if (index > -1) {
                    // Update existing
                    const updated = [...old];
                    updated[index] = project;
                    return updated;
                  } else {
                    // Add new
                    return [project, ...old];
                  }
                }
              );
            } else {
              // If project is now active, remove it from closed projects
              queryClient.setQueryData<Project[]>(
                projectQueryKeys.closed(selectedCompanyId),
                (old) => old?.filter((p) => p.id !== project.id) || []
              );
            }

            // Also invalidate the active projects query in case status changed
            queryClient.invalidateQueries({
              queryKey: projectQueryKeys.active(selectedCompanyId)
            });
          } else if (payload.eventType === "DELETE") {
            // Remove from cache
            queryClient.setQueryData<Project[]>(
              projectQueryKeys.closed(selectedCompanyId),
              (old) => old?.filter((p) => p.id !== payload.old?.id) || []
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedCompanyId, queryClient, options?.enabled]);

  return query;
}

/**
 * Hook to fetch a single project by ID
 */
export function useProject(projectId: string | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery<Project>({
    queryKey: projectQueryKeys.detail(projectId || ""),
    queryFn: () => {
      if (!projectId) throw new Error("No project ID provided");
      return projectsApi.getProject(projectId);
    },
    enabled: !!projectId,
    staleTime: SINGLE_PROJECT_STALE_TIME,
    gcTime: SINGLE_PROJECT_GC_TIME,
  });

  // Set up real-time subscription for this specific project
  useEffect(() => {
    if (!projectId) return;

    const supabase = createClient();

    const channel = supabase
      .channel(`project-${projectId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "projects",
          filter: `id=eq.${projectId}`,
        },
        (payload: RealtimePostgresChangesPayload<Project>) => {
          // Update the cache with the updated project
          queryClient.setQueryData<Project>(
            projectQueryKeys.detail(projectId),
            payload.new as Project
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [projectId, queryClient]);

  return query;
}